/**
 * Map snapshot utilities — effect layer.
 *
 * Why this file exists:
 * - `toBoundsLiteral` and `createCameraSnapshot` consume live Google Maps SDK objects
 *   (LatLngBounds, Map). They are effect-boundary functions, not pure utilities.
 * - Keeping them separate from utils.js ensures that module stays free of SDK coupling.
 *
 * These functions should only be called from effect boundaries (adapters, orchestrators)
 * that already hold a reference to a live map instance.
 */

/**
 * Converts a live Google Maps LatLngBounds object into a plain bounds literal.
 *
 * @param {google.maps.LatLngBounds|null} bounds
 * @returns {{ north: number, south: number, east: number, west: number } | null}
 */
export function toBoundsLiteral(bounds) {
  if (!bounds) {
    return null;
  }

  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();

  return {
    east: northEast.lng(),
    north: northEast.lat(),
    south: southWest.lat(),
    west: southWest.lng(),
  };
}

/**
 * Captures a normalized, comparable snapshot of the current camera state.
 * Centers are rounded to 6 decimal places to suppress sub-pixel drift.
 *
 * @param {google.maps.Map} map
 * @param {boolean} isInteracting
 * @returns {Object|null}
 */
export function createCameraSnapshot(map, isInteracting) {
  if (!map) {
    return null;
  }

  const center = map.getCenter?.();
  const bounds = toBoundsLiteral(map.getBounds?.());

  return {
    zoom: map.getZoom?.() ?? 0,
    centerLat: center ? Number(center.lat().toFixed(6)) : null,
    centerLng: center ? Number(center.lng().toFixed(6)) : null,
    boundsNorth: bounds?.north ?? null,
    boundsSouth: bounds?.south ?? null,
    boundsEast: bounds?.east ?? null,
    boundsWest: bounds?.west ?? null,
    isInteracting,
  };
}

/**
 * Compares two camera snapshots for structural equality.
 * Returns false if either snapshot is null.
 *
 * @param {Object|null} left
 * @param {Object|null} right
 * @returns {boolean}
 */
export function isCameraSnapshotEqual(left, right) {
  if (!left || !right) {
    return false;
  }

  return (
    left.zoom === right.zoom &&
    left.centerLat === right.centerLat &&
    left.centerLng === right.centerLng &&
    left.boundsNorth === right.boundsNorth &&
    left.boundsSouth === right.boundsSouth &&
    left.boundsEast === right.boundsEast &&
    left.boundsWest === right.boundsWest &&
    left.isInteracting === right.isInteracting
  );
}
